import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, MapPin, Wifi, Download, Mail, Briefcase, GraduationCap, Languages } from "lucide-react";

export default function CandidateDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [countries, setCountries] = useState({});

  useEffect(() => {
    api.get(`/candidates/${id}`).then((r) => setData(r.data));
    api.get("/countries").then((r) => setCountries(Object.fromEntries(r.data.map((c) => [c.code, c]))));
  }, [id]);

  if (!data) return <div className="max-w-3xl mx-auto p-10 text-slate-500">Chargement...</div>;
  const { user, profile } = data;

  const cvDownloadUrl = profile?.cv_path
    ? `${API}/files/${profile.cv_path}?auth=${localStorage.getItem("mec_token")}`
    : null;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="candidate-detail">
      <Link to="/candidates" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour
      </Link>

      <div className="mec-card p-8">
        <div className="flex items-start gap-5 mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
            {user.full_name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl lg:text-3xl font-bold text-slate-900">{user.full_name}</h1>
              {profile?.is_premium && (
                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200" data-testid="cand-premium-badge">
                  ★ Premium
                </Badge>
              )}
            </div>
            <div className="text-slate-600 text-sm mt-1">{profile?.headline || profile?.desired_role}</div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-2">
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{countries[user.country_code]?.flag} {countries[user.country_code]?.name}</span>
              {profile?.open_to_remote && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200"><Wifi className="w-3 h-3 mr-1" />Ouvert au remote</Badge>}
              {profile?.desired_contract && <Badge variant="outline">{profile.desired_contract}</Badge>}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <Link to={`/messages/${user.id}`}>
              <Button size="sm" className="bg-[#004EEB] hover:bg-[#003BCC]" data-testid="contact-btn">
                <Mail className="w-4 h-4 mr-1.5" /> Contacter
              </Button>
            </Link>
            {cvDownloadUrl && (
              <a href={cvDownloadUrl} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" data-testid="cv-download-btn">
                  <Download className="w-4 h-4 mr-1.5" /> CV
                </Button>
              </a>
            )}
          </div>
        </div>

        {profile?.bio && (
          <div className="mb-6 pb-6 border-b border-slate-200">
            <div className="mec-overline mb-2">À propos</div>
            <p className="text-slate-700 whitespace-pre-line leading-relaxed">{profile.bio}</p>
          </div>
        )}

        {profile?.skills?.length > 0 && (
          <div className="mb-6">
            <div className="mec-overline mb-2">Compétences</div>
            <div className="flex flex-wrap gap-2">
              {profile.skills.map((s, i) => <Badge key={i} variant="outline" className="border-slate-300">{s}</Badge>)}
            </div>
          </div>
        )}

        {profile?.experiences?.length > 0 && (
          <div className="mb-6">
            <div className="mec-overline mb-3 inline-flex items-center gap-1.5"><Briefcase className="w-3 h-3" /> Expériences</div>
            <div className="space-y-3">
              {profile.experiences.map((exp, i) => (
                <div key={i} className="border-l-2 border-slate-200 pl-4">
                  <div className="font-semibold text-slate-900">{exp.title}</div>
                  <div className="text-sm text-slate-600">{exp.company} • {exp.start} → {exp.end || "Présent"}</div>
                  {exp.description && <div className="text-sm text-slate-600 mt-1 whitespace-pre-line">{exp.description}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {profile?.education?.length > 0 && (
          <div className="mb-6">
            <div className="mec-overline mb-3 inline-flex items-center gap-1.5"><GraduationCap className="w-3 h-3" /> Formation</div>
            <div className="space-y-3">
              {profile.education.map((ed, i) => (
                <div key={i} className="border-l-2 border-slate-200 pl-4">
                  <div className="font-semibold text-slate-900">{ed.degree}</div>
                  <div className="text-sm text-slate-600">{ed.school} • {ed.start} → {ed.end}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {profile?.languages?.length > 0 && (
          <div>
            <div className="mec-overline mb-2 inline-flex items-center gap-1.5"><Languages className="w-3 h-3" /> Langues</div>
            <div className="flex flex-wrap gap-2">
              {profile.languages.map((l, i) => <Badge key={i} variant="outline" className="border-slate-300">{l}</Badge>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
